import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
const CONTEXT_ATTRIBUTES = {
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
    depth: true,
    stencil: false,
};
function getOrCreateContext(canvas) {
    const context = canvas.getContext('webgl2', CONTEXT_ATTRIBUTES) ??
        canvas.getContext('webgl', CONTEXT_ATTRIBUTES);
    return (context ?? undefined);
}
/**
 * Persistent Stage WebGL runtime: one canvas, one renderer, one animation loop.
 * Scene effects and future 3D objects attach as groups inside this scene.
 * The renderer is created with the Stage and disposed only on unmount —
 * toggling the last effect must not tear down the context.
 */
export class StageRuntime {
    renderer;
    scene;
    camera;
    objectsLayer;
    effectsLayer;
    updates = new Set();
    timer = new THREE.Timer();
    frameId = 0;
    running = false;
    disposed = false;
    constructor(canvas, width, height) {
        this.renderer = new THREE.WebGLRenderer({
            canvas,
            context: getOrCreateContext(canvas),
            alpha: true,
            antialias: true,
            premultipliedAlpha: false,
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.setClearColor(0x000000, 0);
        this.scene = new THREE.Scene();
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
        this.camera.position.z = 10;
        this.objectsLayer = new THREE.Group();
        this.objectsLayer.name = 'objects';
        this.objectsLayer.renderOrder = 0;
        this.effectsLayer = new THREE.Group();
        this.effectsLayer.name = 'effects';
        this.effectsLayer.renderOrder = 1000;
        this.scene.add(this.objectsLayer);
        this.scene.add(this.effectsLayer);
        this.timer.connect(document);
        this.resize(width, height);
    }
    get isDisposed() {
        return this.disposed;
    }
    resize(width, height) {
        if (this.disposed || width <= 0 || height <= 0)
            return;
        this.renderer.setSize(width, height, false);
        this.camera.left = -width / 2;
        this.camera.right = width / 2;
        this.camera.top = height / 2;
        this.camera.bottom = -height / 2;
        this.camera.updateProjectionMatrix();
        if (!this.running)
            this.renderFrame();
    }
    addUpdate(fn) {
        this.updates.add(fn);
        return () => {
            this.updates.delete(fn);
        };
    }
    /** Start or stop the loop from layer occupancy — empty scene stays transparent, context stays alive. */
    syncLoop() {
        if (this.disposed)
            return;
        const needsLoop = this.objectsLayer.children.length > 0 || this.effectsLayer.children.length > 0;
        if (needsLoop)
            this.start();
        else
            this.stopAndClear();
    }
    dispose() {
        if (this.disposed)
            return;
        this.stop();
        this.disposed = true;
        this.updates.clear();
        this.effectsLayer.clear();
        this.objectsLayer.clear();
        this.scene.clear();
        this.timer.dispose();
        this.renderer.dispose();
    }
    start() {
        if (this.running || this.disposed)
            return;
        this.running = true;
        this.timer.reset();
        const animate = () => {
            if (!this.running)
                return;
            this.frameId = requestAnimationFrame(animate);
            this.timer.update();
            const delta = this.timer.getDelta();
            this.updates.forEach((fn) => fn(delta));
            this.renderFrame();
        };
        animate();
    }
    stopAndClear() {
        this.stop();
        this.renderFrame();
    }
    stop() {
        this.running = false;
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
            this.frameId = 0;
        }
    }
    renderFrame() {
        if (this.disposed)
            return;
        this.renderer.render(this.scene, this.camera);
    }
}
export function useStageRuntime(canvasRef, width, height) {
    const [runtime, setRuntime] = useState(null);
    const sizeRef = useRef({ width, height });
    sizeRef.current = { width, height };
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const { width: nextWidth, height: nextHeight } = sizeRef.current;
        const next = new StageRuntime(canvas, Math.max(nextWidth, 1), Math.max(nextHeight, 1));
        setRuntime(next);
        return () => {
            next.dispose();
            setRuntime(null);
        };
    }, [canvasRef]);
    useEffect(() => {
        if (!runtime || width <= 0 || height <= 0)
            return;
        runtime.resize(width, height);
    }, [runtime, width, height]);
    return runtime;
}
